import axios from "axios";
import { findCustomerByPhone, saveCustomerSale, customerRequestOptions } from "./customerService";
import { TRUNG_THU_LOAI_BANH, TRUNG_THU_LOAI_PHIEU } from "../constants/trungThu";

export const POS_TRUNG_THU_API_BASE_URL = (
  process.env.REACT_APP_POS_API_BASE_URL || `${process.env.REACT_APP_API_BASE_URL}`
).replace(/\/+$/, "");

export const PAYMENT_METHODS = [
  { id: "cash", label: "Tiền mặt", apiValue: "0", note: "Tiền mặt" },
  { id: "transfer", label: "Chuyển khoản", apiValue: "1", note: "Chuyển khoản" },
  { id: "card", label: "VISA", apiValue: "1.1", note: "Quẹt thẻ" },
  { id: "momo", label: "Momo", apiValue: "1.2", note: "Ví Momo" },
  { id: "vnpay", label: "VNPAY", apiValue: "1.3", note: "Ví Vnpay" },
  { id: "Tiktok", label: "Tiktok", apiValue: "3", note: "Tiktok" },
];

export const getPaymentMeta = (paymentMethod) =>
  PAYMENT_METHODS.find((item) => item.id === paymentMethod) || PAYMENT_METHODS[0];

const toProductPrice = (value) => {
  const price = Number(value);
  return Number.isFinite(price) ? price : 0;
};

export const getTrungThuItemsByPaymentMethod = (items, paymentMethod) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    price:
      paymentMethod === "Tiktok"
        ? toProductPrice(
            item.tiktokPrice ??
              item.raw?.GiaGrab ??
              item.regularPrice ??
              item.price,
          )
        : toProductPrice(item.regularPrice ?? item.raw?.DonGia ?? item.price),
  }));

export const extractLoSXFromQRCode = (qrData) => {
  const value = String(qrData || "").trim();
  if (!value) return "";

  try {
    const url = new URL(value);
    const code = url.searchParams.get("c");
    if (code) return code.trim();
  } catch (error) {
    // QR can be a URL fragment, a plain LoSX, or text copied from a scanner.
  }

  const queryMatch = value.match(/[?&]c=(\d+)/i);
  if (queryMatch) return queryMatch[1];

  const doticomMatch = value.match(/doticom\.vn\/t\.php\?c=(\d+)/i);
  if (doticomMatch) return doticomMatch[1];

  const loSearchMatch = value.match(/^lo\s*(\d+)$/i);
  if (loSearchMatch) return loSearchMatch[1];

  return /^\d+$/.test(value) ? value : "";
};

export const fetchTrungThuProductByQR = async (qrData) => {
  const loSX = extractLoSXFromQRCode(qrData);
  if (!loSX) {
    throw new Error("Không đọc được mã lô sản xuất từ QR.");
  }

  const response = await axios.get(`${POS_TRUNG_THU_API_BASE_URL}/api/QRTrungThu`, {
    params: { LoSX: loSX },
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  const product = Array.isArray(response.data) ? response.data[0] : response.data;
  if (!product || !product.MaSP) {
    throw new Error(`Không tìm thấy sản phẩm với lô ${loSX}.`);
  }

  const regularPrice = toProductPrice(product.DonGia);
  const tiktokPrice = toProductPrice(product.GiaGrab);

  return {
    key: loSX,
    qr: String(qrData || "").trim(),
    loSX,
    id: product.MaSP,
    name: product.TenSP || product.Ten_SP || `Sản phẩm ${product.MaSP}`,
    price: regularPrice,
    regularPrice,
    tiktokPrice,
    unit: product.DVT_SP || product.DVT || "cái",
    vat: Number(product.VAT_SP) || 0,
    category: product.MaNLSP || "TRUNGTHU_QR",
    expiryDate:
      product.NgaySD ||
      product.NgayHetHan ||
      product.HanSD ||
      product.HSD ||
      "",
    quantity: 1,
    raw: product,
  };
};

export const fetchVoucher = async (voucherCode, customer) => {
  if (!customer?.SDT) throw new Error("Vui lòng chọn khách hàng trước khi áp dụng Voucher.");
  const response = await axios.get(
    `${POS_TRUNG_THU_API_BASE_URL}/api/customers/vouchers`,
    customerRequestOptions({ params: { phone: customer.SDT, code: voucherCode } }),
  );

  const vouchers = response.data?.data || [];
  return vouchers.length === 1 && vouchers[0].status === "available" ? vouchers[0] : null;
};

export const fetchCustomerByPhone = (phone) => findCustomerByPhone(POS_TRUNG_THU_API_BASE_URL, phone);

export const fetchTrungThuStores = async () => {
  const response = await axios.get(`${POS_TRUNG_THU_API_BASE_URL}/stores`, {
    timeout: 8000,
  });
  return Array.isArray(response.data) ? response.data : [];
};

export const calculateVoucherDiscount = (voucher, subtotal) => {
  if (!voucher) return 0;

  const fixedAmount = Number(voucher.TienCK) || 0;
  if (fixedAmount > 0) return fixedAmount;

  const percent = Number(voucher.ChietKhau) || 0;
  if (percent <= 0) return 0;

  const safePercent = Math.max(0, Math.min(100, percent));
  return Math.floor((Number(subtotal) || 0) * (safePercent / 100));
};

const getStoredCustomerInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("currentCustomerInfo") || "{}");
  } catch (error) {
    return {};
  }
};

export const buildTrungThuBillItems = (items, cakeType) =>
  items.map((item) => {
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.price) || 0;
    const discountPercent = Math.max(
      0,
      Math.min(100, Number(item.discountPercent) || 0),
    );

    return {
      id: item.id,
      name: `${item.name}${item.unit ? " [" + item.unit + "]" : ""}`,
      quantity,
      unitPrice,
      KmaiPT: 0,
      ThanhTien: quantity * unitPrice * (1 - discountPercent / 100),
      MXTY: 0,
      LoSX: item.loSX || "",
      LoaiBanh: TRUNG_THU_LOAI_BANH,
      LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
      note: cakeType,
    };
  });

const getLocalDateString = (date = new Date()) => {
  const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
};

const toApiNumberOrString = (value) => {
  const normalizedValue = String(value ?? "").trim();
  const numberValue = Number(normalizedValue);
  return normalizedValue && Number.isFinite(numberValue) ? numberValue : normalizedValue;
};

export const submitTrungThuSale = async ({
  items,
  cakeType,
  subtotal,
  total,
  paymentMethod,
  cashGiven,
  discountPercent,
  voucherDiscount,
  voucherCode,
  usePointDiscount,
  billNote,
  customerInfo: selectedCustomer,
}) => {
  const paymentMeta = getPaymentMeta(paymentMethod);
  const customerInfo = selectedCustomer === undefined ? getStoredCustomerInfo() : selectedCustomer;
  const IDCH = localStorage.getItem("IDCH") || "unknown_store_id";
  const TenNV = localStorage.getItem("TenUser") || "Unknown User";
  const ipBanhNgot = localStorage.getItem("IpBanhNgot") || "";
  const cashGivenValue = parseInt(cashGiven || "0", 10) || 0;
  const effectiveGiamPT = Math.max(0, Math.min(100, Number(discountPercent) || 0));
  const totalAmount = Math.max(Number(total) || 0, 0);

  const billDetails = {
    storeId: IDCH,
    totalAmount,
    cashGiven: cashGivenValue,
    change: cashGivenValue === 0 ? 0 : cashGivenValue - totalAmount,
    MaTheVIP: customerInfo?.MaKH || "",
    GiamPT: effectiveGiamPT,
    GiamTienMat: 0,
    GiamTMTheVIP: Number(voucherDiscount) || 0,
    VoucherCode: voucherCode || "",
    employeeName: TenNV,
    TenKH: customerInfo?.Ten || "Khách lẻ",
    MaKH: customerInfo?.MaKH || "",
    ipBanhNgot,
    HeSoTichDiem: customerInfo?.HeSo_TichDiem ?? "0.02",
    TieuDiem: Number(usePointDiscount) || 0,
    PhuongThucThanhToan: paymentMeta.apiValue,
    GhiChu: 'Bánh trung thu',
    LoaiBanh: TRUNG_THU_LOAI_BANH,
    LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
    items: buildTrungThuBillItems(items, cakeType),
    customerInfo: customerInfo?.MaKH ? customerInfo : undefined,
  };

  let puller = null;
  if (paymentMeta.apiValue === "2") {
    puller = 0;
  } else if (["1", "1.1", "1.2", "1.3", "1.4", "1.5"].includes(paymentMeta.apiValue)) {
    puller = 1;
  }

  const response = await saveCustomerSale(POS_TRUNG_THU_API_BASE_URL, {
    billDetails, puller, LoaiBanh: TRUNG_THU_LOAI_BANH, LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
  });
  // Voucher consumption is part of the bill transaction.

  return response.data;
};

export const submitTrungThuInventoryCheck = async ({ items, note = "kiemke-btt" }) => {
  const storeId = localStorage.getItem("IDCH") || "";
  const userCode = localStorage.getItem("MaNV") || localStorage.getItem("TenUser") || "";

  if (!storeId) {
    throw new Error("Không tìm thấy mã cửa hàng.");
  }

  if (!userCode) {
    throw new Error("Không tìm thấy thông tin người kiểm kê.");
  }

  const payload = {
    MaCH: toApiNumberOrString(storeId),
    NgayKiemKe: getLocalDateString(),
    TenUser: userCode,
    GhiChu: note,
    ChiTiet: items.map((item) => ({
      LoSX: toApiNumberOrString(item.loSX),
      TonThucTe: Number(item.quantity) || 0,
    })),
  };

  const response = await axios.post(
    `${POS_TRUNG_THU_API_BASE_URL}/api/phieu-kiem-ke-btt`,
    payload,
    { headers: { "Content-Type": "application/json" } },
  );

  return response.data;
};

export const submitTrungThuStockAction = async ({
  actionType,
  items,
  targetStoreId,
  targetStoreName,
}) => {
  const endpointByType = {
    transfer: "/api/dieuchuyen",
    return: "/api/trave",
  };

  const endpoint = endpointByType[actionType];
  if (!endpoint) {
    throw new Error("Loại nghiệp vụ không hợp lệ.");
  }

  const sourceStoreId = localStorage.getItem("IDCH") || "";
  const isTransfer = actionType === "transfer";
  const payload = {
    actionType,
    storeId: sourceStoreId,
    employeeId: localStorage.getItem("MaNV") || "",
    employeeName: localStorage.getItem("TenUser") || "",
    createdAt: new Date().toISOString(),
    LoaiBanh: TRUNG_THU_LOAI_BANH,
    LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
    ...(isTransfer
      ? {
          targetStoreId: targetStoreId || "",
          targetStoreName: targetStoreName || "",
          receivingStoreId: targetStoreId || "",
          MaCH: targetStoreId || "",
          MaCH_DC: sourceStoreId,
        }
      : {}),
    items: items.map((item) => ({
      qr: item.qr,
      LoSX: item.loSX,
      MaSP: item.id,
      TenSP: item.name,
      SoLuong: Number(item.quantity) || 0,
      DonGia: Number(item.price) || 0,
      LoaiBanh: TRUNG_THU_LOAI_BANH,
      LoaiPhieu: TRUNG_THU_LOAI_PHIEU,
      ...(isTransfer
        ? {
            MaCH: targetStoreId || "",
            MaCH_DC: sourceStoreId,
          }
        : {}),
    })),
  };

  const response = await axios.post(`${POS_TRUNG_THU_API_BASE_URL}${endpoint}`, payload, {
    headers: { "Content-Type": "application/json" },
  });

  return response.data;
};
